export type PermissionKey =
  | "CapitalCall"
  | "Distribution"
  | "FinancialReporting"
  | "LegalInformation"
  | "TaxInformation"
  | "GeneralCarlyleInformation"
  | "InvestorConferencePresentations"
  | "NotificationsService"
  | "PrivilegedInvestorAccess"
  | "AlpInvestFinancialDocuments"
  | "AlpInvestFinancialReporting"
  | "AlpInvestTaxInformation"
  | "DealSummaries"
  | "QuarterlyReporting"
  | "Invoices"
  | "AmlKycInformation";

export type FundBucket = "A" | "B";

export interface PermissionIdentifier {
  mcServiceId: number;
  mcNotifId: number | null;
  mpService: string;
  mpNotif: string | null;
}

export const PERMISSION_IDENTIFIERS: Record<PermissionKey, PermissionIdentifier> = {
  CapitalCall: {
    mcServiceId: 8,
    mcNotifId: 4,
    mpService: "Capital Call",
    mpNotif: "Capital Call Management Notification",
  },
  Distribution: {
    mcServiceId: 10,
    mcNotifId: 7,
    mpService: "Distribution",
    mpNotif: "Distribution Management Notification",
  },
  FinancialReporting: {
    mcServiceId: 11,
    mcNotifId: 8,
    mpService: "Financial Reporting",
    mpNotif: "Financial Reporting Notification",
  },
  LegalInformation: {
    mcServiceId: 16,
    mcNotifId: 13,
    mpService: "Legal Information",
    mpNotif: "Legal Information Notification",
  },
  TaxInformation: {
    mcServiceId: 19,
    mcNotifId: 17,
    mpService: "Tax Information",
    mpNotif: "Tax Information Notification",
  },
  GeneralCarlyleInformation: {
    mcServiceId: 12,
    mcNotifId: 9,
    mpService: "General Carlyle Information",
    mpNotif: "GeneralCarlyleInformationNotification",
  },
  InvestorConferencePresentations: {
    mcServiceId: 14,
    mcNotifId: 11,
    mpService: "Investor Conference Presentations",
    mpNotif: "InvestorConferencePresentationsNotification",
  },
  NotificationsService: {
    mcServiceId: 18,
    mcNotifId: 14,
    mpService: "Notifications",
    mpNotif: "NotificationsServiceNotification",
  },
  PrivilegedInvestorAccess: {
    mcServiceId: 21,
    mcNotifId: 15,
    mpService: "Privileged Investor Access",
    mpNotif: "PrivilegedInvestorAccessNotification",
  },
  AlpInvestFinancialDocuments: {
    mcServiceId: 1000003,
    mcNotifId: 2,
    mpService: "AlpInvest Financial Documents",
    mpNotif: "AlpInvestFinancialDocumentsNotification",
  },
  AlpInvestFinancialReporting: {
    mcServiceId: 1000002,
    mcNotifId: null,
    mpService: "AlpInvest Financial Reporting",
    mpNotif: null,
  },
  AlpInvestTaxInformation: {
    mcServiceId: 1000004,
    mcNotifId: null,
    mpService: "AlpInvest Tax Information",
    mpNotif: null,
  },
  DealSummaries: {
    mcServiceId: 1000005,
    mcNotifId: 6,
    mpService: "Deal Summaries",
    mpNotif: "DealSummariesNotification",
  },
  QuarterlyReporting: {
    mcServiceId: 1000006,
    mcNotifId: 16,
    mpService: "Quarterly Reporting",
    mpNotif: "QuarterlyReportingNotification",
  },
  Invoices: {
    mcServiceId: 1000007,
    mcNotifId: 12,
    mpService: "Invoices",
    mpNotif: "InvoicesNotification",
  },
  AmlKycInformation: {
    mcServiceId: 1000018,
    mcNotifId: 1001,
    mpService: "AML KYC Information",
    mpNotif: "AMLKYCInformationNotification",
  },
};

export const VISIBLE_KEYS: ReadonlySet<PermissionKey> = new Set<PermissionKey>([
  "CapitalCall",
  "Distribution",
  "FinancialReporting",
  "LegalInformation",
  "TaxInformation",
]);

export const isVisibleKey = (key: PermissionKey): boolean => VISIBLE_KEYS.has(key);
export const isHiddenKey = (key: PermissionKey): boolean => !VISIBLE_KEYS.has(key);

// The full universe of visible (core-5) display strings. Used by the on-screen cascade so a role
// change recomputes the entire visible set from the selected roles rather than only releasing the
// previously selected role's grant.
export const VISIBLE_SERVICE_DISPLAY_STRINGS: ReadonlySet<string> = new Set(
  Array.from(VISIBLE_KEYS).map((k) => PERMISSION_IDENTIFIERS[k].mpService)
);

export const VISIBLE_NOTIFICATION_DISPLAY_STRINGS: ReadonlySet<string> = new Set(
  Array.from(VISIBLE_KEYS)
    .map((k) => PERMISSION_IDENTIFIERS[k].mpNotif)
    .filter((s): s is string => s !== null)
);

const ALL_PERMISSION_KEYS = Object.keys(PERMISSION_IDENTIFIERS) as PermissionKey[];

// Display strings for every hidden, cascade-managed permission. Under membership-replace these are
// derived from the selected role on each save, so a prior role's hidden grants must be dropped (and
// re-added by the cascade) rather than preserved — otherwise a stale hidden value such as Investor
// Conference Presentations survives a role change that no longer grants it.
export const HIDDEN_MANAGED_SERVICE_DISPLAY_STRINGS: ReadonlySet<string> = new Set(
  ALL_PERMISSION_KEYS.filter(isHiddenKey).map((k) => PERMISSION_IDENTIFIERS[k].mpService)
);

export const HIDDEN_MANAGED_NOTIFICATION_DISPLAY_STRINGS: ReadonlySet<string> = new Set(
  ALL_PERMISSION_KEYS.filter(isHiddenKey)
    .map((k) => PERMISSION_IDENTIFIERS[k].mpNotif)
    .filter((s): s is string => s !== null)
);

interface Grant {
  service: PermissionKey;
  notif: PermissionKey | null;
}

const pair = (key: PermissionKey): Grant => ({ service: key, notif: key });

// A = Equity (internal IFC). B = Standard Carlyle (external IFC). Visible core-5 only -
// hidden grants are derived server-side from roles + Product_Line and must not be mirrored here.
export const ROLE_GRANTS: Record<FundBucket, Record<string, readonly Grant[]>> = {
  A: {
    "Primary Contact": [
      pair("CapitalCall"),
      pair("Distribution"),
      pair("FinancialReporting"),
      pair("LegalInformation"),
      pair("TaxInformation"),
    ],
    "Secondary Contact": [
      pair("CapitalCall"),
      pair("Distribution"),
      pair("FinancialReporting"),
      pair("LegalInformation"),
      pair("TaxInformation"),
    ],
    "Tax Advisor": [pair("TaxInformation")],
    "Legal Advisor": [],
    "Finance Contact": [],
    "AML/KYC Contact": [],
    "Wire Approver": [],
  },
  B: {
    "Primary Contact": [
      pair("CapitalCall"),
      pair("Distribution"),
      pair("FinancialReporting"),
      pair("LegalInformation"),
      pair("TaxInformation"),
    ],
    "Secondary Contact": [
      pair("CapitalCall"),
      pair("Distribution"),
      pair("FinancialReporting"),
      pair("LegalInformation"),
      pair("TaxInformation"),
    ],
    "Finance Contact": [pair("CapitalCall"), pair("Distribution"), pair("FinancialReporting")],
    "Tax Advisor": [pair("TaxInformation")],
    "Legal Advisor": [pair("LegalInformation")],
    "AML/KYC Contact": [],
    "Wire Approver": [pair("CapitalCall"), pair("Distribution")],
  },
};

export interface RoleCascadeResult {
  services: PermissionKey[];
  notifications: PermissionKey[];
}

export const computeRoleCascade = (roles: readonly string[], bucket: FundBucket): RoleCascadeResult => {
  const services = new Set<PermissionKey>();
  const notifications = new Set<PermissionKey>();
  const grants = ROLE_GRANTS[bucket];

  for (const role of roles) {
    const roleGrants = grants[role];

    if (!roleGrants) continue;

    for (const grant of roleGrants) {
      services.add(grant.service);
      if (grant.notif !== null) notifications.add(grant.notif);
    }
  }

  return { services: Array.from(services), notifications: Array.from(notifications) };
};

export const bucketForFund = (isExternal: boolean): FundBucket => (isExternal ? "B" : "A");

export const cascadeVisibleDisplayStrings = (
  result: RoleCascadeResult
): { services: string[]; notifications: string[] } => ({
  services: result.services.filter(isVisibleKey).map((k) => PERMISSION_IDENTIFIERS[k].mpService),
  notifications: result.notifications
    .filter(isVisibleKey)
    .map((k) => PERMISSION_IDENTIFIERS[k].mpNotif)
    .filter((s): s is string => s !== null),
});
